/**
 * RegionalSalesKpis — 4-up KPI strip for /r/:slug/sales. Port of the
 * V3Sales KPI grid: Weekly ticket sales (with Region / Subregion /
 * Venue scope picker), Ad spend · this week, Web visits, QR scans.
 *
 * Label resolution uses the per-design-label candidate list (playbook
 * §2). The scope picker is local state — per-scope values ship with
 * BON-138; until then every scope resolves to the same DTO cell (or
 * an em-dash), so the toggle is harmless.
 */
import { useState } from 'react';

import { SegmentedControl } from '../primitives';

import type { RegionalSalesDto } from './regionalApi';

const EM_DASH = '—';

type SalesScope = 'region' | 'subregion' | 'venue';

const SCOPE_OPTIONS: ReadonlyArray<{ value: SalesScope; label: string }> = [
  { value: 'region', label: 'Region' },
  { value: 'subregion', label: 'Subregion' },
  { value: 'venue', label: 'Venue' },
];

// TODO(BON-138): collapse to exact design labels when the DTO ships them.
const KPI_LABEL_CANDIDATES: Record<string, readonly string[]> = {
  'Weekly ticket sales': ['weekly ticket sales', 'revenue this week'],
  'Ad spend · this week': ['ad spend'],
  'Web visits': ['web visits', 'site visits'],
  'QR scans': ['qr scans'],
};

/** First DTO cell whose label contains a candidate substring, or undefined. */
export function findSalesKpi(
  kpis: RegionalSalesDto['kpis'],
  designLabel: keyof typeof KPI_LABEL_CANDIDATES,
): RegionalSalesDto['kpis'][number] | undefined {
  for (const needle of KPI_LABEL_CANDIDATES[designLabel]) {
    const found = kpis.find((k) => k.label.toLowerCase().includes(needle));
    if (found) return found;
  }
  return undefined;
}

function DeltaLine({ kpi, sub }: { kpi?: RegionalSalesDto['kpis'][number]; sub: string }) {
  const direction = kpi?.delta?.direction;
  const cls =
    direction === 'up' ? 'nb-kpi-delta good' : direction === 'down' ? 'nb-kpi-delta bad' : '';
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 3 }}>
      {kpi?.delta && (
        <span className={cls} style={{ fontSize: 11 }}>
          {kpi.delta.value}
        </span>
      )}
      <span style={{ fontSize: 11, color: 'var(--nb-subtle)' }}>{sub}</span>
    </div>
  );
}

function Value({ kpi }: { kpi?: RegionalSalesDto['kpis'][number] }) {
  return (
    <div
      className="nb-tabular"
      style={{
        fontSize: 26,
        fontWeight: 700,
        letterSpacing: '-0.02em',
        lineHeight: 1.15,
        marginTop: 4,
      }}
    >
      {kpi?.value ?? EM_DASH}
    </div>
  );
}

export function RegionalSalesKpis({ kpis }: { kpis: RegionalSalesDto['kpis'] }) {
  const [scope, setScope] = useState<SalesScope>('region');
  // TODO(BON-138): index per-scope values by `scope` once the DTO ships them.
  const weekly = findSalesKpi(kpis, 'Weekly ticket sales');

  return (
    <div className="nb-kpi-strip" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
      <div className="nb-kpi-cell">
        <div
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}
        >
          <div className="nb-eyebrow" style={{ fontSize: 10 }}>
            Weekly ticket sales
          </div>
          <SegmentedControl
            value={scope}
            onChange={setScope}
            options={SCOPE_OPTIONS}
            ariaLabel="Weekly ticket sales scope"
          />
        </div>
        <Value kpi={weekly} />
        <DeltaLine kpi={weekly} sub={`this week, ${scope}`} />
      </div>
      {(['Ad spend · this week', 'Web visits', 'QR scans'] as const).map((label) => {
        const kpi = findSalesKpi(kpis, label);
        return (
          <div key={label} className="nb-kpi-cell">
            <div className="nb-eyebrow" style={{ fontSize: 10 }}>
              {label}
            </div>
            <Value kpi={kpi} />
            <DeltaLine kpi={kpi} sub={label === 'Ad spend · this week' ? 'vs prev wk' : '7-day'} />
          </div>
        );
      })}
    </div>
  );
}
